import React, { useState, useEffect } from 'react';
import { StringListEditor, SourceField, KeyedValue, toKeyed, fromKeyed } from './NotesEditor';
import { X, Users, CheckCircle } from 'lucide-react';
import { Feature } from '../../services/workshop';

interface FeatureEditModalProps {
  feature: Feature;
  createMode?: boolean;
  personas: { id: string; name: string }[];
  onSave: (featureId: string, patch: Partial<Feature>) => Promise<void>;
  onClose: () => void;
}

export function FeatureEditModal({ feature, createMode, personas, onSave, onClose }: FeatureEditModalProps) {
  const [name, setName] = useState(feature.name);
  const [description, setDescription] = useState(feature.description);
  const [relatedPersonas, setRelatedPersonas] = useState<string[]>(feature.relatedPersonas || []);
  const [status, setStatus] = useState<NonNullable<Feature['status']>>(feature.status || 'draft');
  const [notes, setNotes] = useState<KeyedValue[]>(toKeyed(feature.notes, 'note'));
  const [source, setSource] = useState(feature.source || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave(feature.id, {
        name: name.trim(),
        description: description.trim(),
        relatedPersonas,
        status,
        notes: fromKeyed(notes),
        source: source.trim(),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const togglePersona = (personaName: string) => {
    setRelatedPersonas(prev =>
      prev.includes(personaName) ? prev.filter(p => p !== personaName) : [...prev, personaName]
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h2 className="text-base font-bold text-navy">{createMode ? 'Add Feature' : 'Edit Feature'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-5">
          {/* Name */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Small Business Customer Web Portal"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={5}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
          </div>

          {/* Related personas */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Users size={11} />Related Personas
            </label>
            {personas.length === 0 ? (
              <p className="text-xs text-slate-400">No personas yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {personas.map(p => {
                  const selected = relatedPersonas.includes(p.name);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => togglePersona(p.name)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                        selected
                          ? 'bg-primary/10 border-primary/40 text-primary'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <CheckCircle size={11} />Status
            </label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden w-fit">
              {(['draft', 'confirmed'] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`px-4 py-2 text-xs font-semibold transition-all capitalize ${
                    status === s ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <StringListEditor
            label="Notes"
            items={notes}
            onChange={setNotes}
            idPrefix="note"
            addLabel="Add note"
            multiline
            placeholder="Working note or commentary"
          />

          <SourceField value={source} onChange={setSource} />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
